/**
 * Generates HTML email template for "Payment Pre-Authorized".
 * Sent to clients when a pre-authorization hold is placed on their card
 * for an advance / additional-services invoice (funds reserved, not charged).
 * Uses shared layout from emailLayout.js for header/footer.
 */

import { spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter, icon } from '../../emailLayout';
import { trunc2 } from '@/utils';

export const buildInvoicePaymentSecuredBookingEmailHtml = ({
  invoice,
  sales,
  organization
}) => {
  const services = invoice?.items || [];
  const cardInfo = sales?.related_transactions?.sale_completion?.cardAccount || {};
  const client = `${invoice?.client?.first_name} ${invoice?.client?.last_name}`;

  const invoiceSubtotal = parseFloat(invoice?.subtotal) || 0;
  const invoiceTaxAmount = parseFloat(invoice?.tax_amount) || 0;
  const invoicePlatformFee = parseFloat(invoice?.platform_fee)     || 0;
  //const invoiceCardFee = sales?.withOutSales ? 0 : (parseFloat(invoice?.card_fee) || 0);
  const invoiceTaxesAndFees = trunc2(invoiceTaxAmount + invoicePlatformFee);
  const invoiceTotalAmount = trunc2(invoiceSubtotal + invoiceTaxesAndFees);

  const totalCardFee = trunc2(invoiceTotalAmount * 0.03);
  const totalWithCardFee = trunc2(invoiceTotalAmount + totalCardFee);
  const invoiceType = invoice?.invoice_type || '';
  const invoiceMethod = invoice?.preferred_method || '';

  // ── Per-service rows ─────────────────────────────────────────────────────
  const serviceRowsHtml = (() => {
    if (services?.length > 0) {
      return services.map((s, i) => {
        const isLast = i === services.length - 1;
        return `
              <tr>
                <td colspan="2" style="padding:0 20px ${isLast ? '0' : '6px'} 20px;">
                  <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                    <tr>
                      <td style="padding:12px 20px;font-size:14px;font-weight:700;color:#111827;background-color:#F7F7F7;">${s.item || s.name || ''}</td>
                      <td align="right" style="padding:12px 20px;font-size:14px;font-weight:700;color:#111827;background-color:#F7F7F7;">$${s.amount}</td>
                    </tr>
                  </table>
                </td>
              </tr>`;
      }).join('');
    }
    return '';
  })();

  // ── Card brand badge ──────────────────────────────────────────────────────
  const cardBrandBadge = cardInfo?.brand ? `<span style="display:inline-block;background-color:#252525;color:#FFFFFF;font-size:9px;font-weight:700;padding:2px 5px;border-radius:3px;vertical-align:middle;margin-right:6px;">${cardInfo?.brand.toUpperCase()}</span>`
      : '';

  const cardLast4Row = cardInfo?.last4
    ? `<td align="right" style="padding:4px 20px 14px;font-size:13px;color:#6B7280;white-space:nowrap;">${cardBrandBadge}•••• •••• •••• ${cardInfo?.last4}</td>`
    : '<td></td>';

  const orgStrong = organization?.name
    ? `<strong style="color:#111827;">${organization?.name}</strong>`
    : 'your broker';

  return `
${emailDocOpen({ title: 'Payment Successful (Booking Secured)' })}
${emailHeader({ title: 'Booking Secured', organization })}

        ${spacer()}

        <!-- ===== CLIENT / REFERENCE ROW ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:16px 24px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td>
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice For</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${client || 'Valued Client'}</span>
                  </td>
                  <td align="right">
                    <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">TR Number</span><br/>
                    <span style="font-size:16px;font-weight:700;color:#111827;">${invoice?.quote_number || invoice?.invoice_number}</span>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== NOTIFICATION CARD ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td style="padding:28px 28px 32px;">
              <!-- Icon -->
              <table role="presentation" cellpadding="0" cellspacing="0" style="margin-bottom:20px;">
                <tr>
                  <td style="width:48px;height:48px;text-align:center;vertical-align:middle;background-color:#DCFCE7;border-radius:100%;">
                    ${icon('check', 24, '#16A34A')}  
                  </td>
                </tr>
              </table>
              <!-- Body text -->
              ${invoiceMethod=="CREDIT_CARD" ? `
              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
              Your ${invoiceType=="ADVANCE" ? 'booking has' : 'requested additional services have'} been officially confirmed and the pre-authorization hold on your card ending in <strong style="color:#111827;">${cardInfo?.last4}</strong> has been successfuly processed.
              </p>
              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
                Your payment of <strong style="color:#111827;">USD ${totalWithCardFee.toFixed(2)}</strong> has been cleared, and you may consider this email as your formal receipt of payment.
              </p>` : `

              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
              ${invoiceType=="ADVANCE" ? 'Your booking has been officially confirmed.' : 'Your requested additional services have been officially confirmed.'}
              </p>

              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
                Your payment of <strong style="color:#111827;">USD ${invoice?.total_amount}</strong> has been cleared through your preferred payment method, and you may consider this email as your formal receipt of payment.
              </p>

              <p style="margin:0 0 12px;font-size:14px;color:#374151;line-height:1.7;">
                The hold on your credit card for ${totalWithCardFee.toFixed(2)} has been released. The funds should be available within 1 to 7 business days, depending on your bank.
              </p>
              `}

              <p style="margin:0;font-size:14px;color:#374151;line-height:1.7;">
                For further assistance, please reach out to ${orgStrong} or contact your broker directly.
              </p> 
            </td>
          </tr>
        </table>

        ${spacer()}

        <!-- ===== INVOICE DETAILS ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td colspan="2" style="padding:14px 20px 8px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice Details</span>
            </td>
          </tr>
          ${serviceRowsHtml}
          <tr><td colspan="2" style="padding:8px 0 14px 0;"></td></tr>
        </table>

        ${spacer()}

        <!-- ===== PRICE BREAKDOWN ===== -->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;overflow:hidden;border:1px solid #EBEBEB;">
          <tr>
            <td colspan="2" style="padding:14px 20px 4px;">
              <span style="font-size:14px;font-weight:700;color:#111827;">Price Breakdown</span>
            </td>
          </tr>
          <tr>
            <td colspan="2" style="padding:2px 20px 10px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Invoice</span>
            </td>
          </tr>

          <tr>
            <td style="padding:6px 20px 4px;font-size:13px;color:#6B7280;">Subtotal Cost</td>
            <td align="right" style="padding:6px 20px 4px;font-size:13px;color:#6B7280;">$${invoiceSubtotal.toFixed(2)}</td>
          </tr>
          <tr>
            <td style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">Taxes &amp; Fees</td>
            <td align="right" style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">$${invoiceTaxesAndFees.toFixed(2)}</td>
          </tr>

          <tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>

          <tr>
            <td style="padding:14px 20px 14px;font-size:15px;font-weight:700;color:#111827;">Total Amount</td>
            <td align="right" style="padding:14px 20px 14px;font-size:18px;font-weight:700;color:#111827;">$${invoiceMethod=="CREDIT_CARD" ? totalWithCardFee.toFixed(2) : invoiceTotalAmount.toFixed(2)}</td>
          </tr>

          ${!sales?.withOutSales ? `
          <tr>
            <td style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">Credit/Debit Card Fee (3%)</td>
            <td align="right" style="padding:4px 20px 10px;font-size:13px;color:#6B7280;">$${totalCardFee.toFixed(2)}</td>
          </tr>
          <tr>
            <td colspan="2" style="padding:0 20px;"><div style="border-top:1px solid #E5E7EB;"></div></td>
          </tr>
          <tr>
            <td colspan="2" style="padding:10px 20px 4px;">
              <span style="font-size:10px;text-transform:uppercase;letter-spacing:0.7px;color:#9CA3AF;font-weight:600;">Payment Authorization By</span>
            </td>
          </tr>
          <tr>
            <td style="padding:4px 20px 14px;font-size:14px;font-weight:700;color:#111827;">${client}</td>
            ${cardLast4Row}
          </tr>` : ``}

        </table>
        ${spacer()}

${emailFooter()}
${emailDocClose()}`;
};
